import { Debouncer } from "@tanstack/react-pacer";

export interface StateStorage<R = unknown> {
  getItem: (name: string) => string | null | Promise<string | null>;
  setItem: (name: string, value: string) => R;
  removeItem: (name: string) => R;
}

export interface DebouncedStorage<R = unknown> extends StateStorage<R> {
  flush: () => void;
}

export function createMemoryStorage(): {
  readonly getItem: (name: string) => string | null;
  readonly setItem: (name: string, value: string) => void;
  readonly removeItem: (name: string) => void;
} {
  const store = new Map<string, string>();
  return {
    getItem: (name) => store.get(name) ?? null,
    setItem: (name, value) => {
      store.set(name, value);
    },
    removeItem: (name) => {
      store.delete(name);
    },
  };
}

function isStateStorage(
  storage: Partial<StateStorage> | null | undefined,
): storage is StateStorage {
  return (
    storage !== null &&
    storage !== undefined &&
    typeof storage.getItem === "function" &&
    typeof storage.setItem === "function" &&
    typeof storage.removeItem === "function"
  );
}

export function resolveStorage(storage: Partial<StateStorage> | null | undefined): StateStorage {
  return isStateStorage(storage) ? storage : createMemoryStorage();
}

/**
 * The in-memory stand-in used whenever the browser's storage cannot be.
 *
 * Deliberately a module-level singleton rather than one per call: a caller doing
 * get-or-create has to read back what it just wrote, or it mints a fresh
 * identity every time. `deviceRegistryIdentity` is exactly that shape, and a new
 * device key per page load — silently, only in the environments where storage is
 * blocked — is a worse bug than the one being fixed.
 */
const sharedMemoryStorage = createMemoryStorage();

/**
 * `localStorage` that cannot throw, whichever way it fails.
 *
 * There are two distinct failures and only the first is the obvious one:
 *
 * - the **method** throws — Safari in private mode reports a zero quota, so
 *   `setItem` raises;
 * - the **property access** throws — a sandboxed iframe or a storage-blocked
 *   embed raises on `globalThis.localStorage` itself, before `getItem` is ever
 *   reached.
 *
 * The second defeats the usual guard, because `resolveStorage(globalThis.localStorage)`
 * evaluates its argument before the call: the guard never runs. Every access
 * here is therefore inside the try, starting at the property read.
 *
 * Failures fall back rather than propagate. Storage backs preferences, drafts
 * and layout — losing them degrades the session, but letting the throw escape a
 * render is a white screen.
 */
/**
 * The platform's storage, read from whichever global actually carries it.
 *
 * `globalThis.localStorage` and `window.localStorage` are the same object in a
 * browser, but not under the node test environment, where a suite may stub only
 * one of them. Reading both keeps this helper honest against the way the rest of
 * the codebase reaches storage (`window.localStorage`, in 42 places) without
 * forcing every test to stub a global it does not otherwise use.
 *
 * Called only from inside a `try` — the property reads are themselves what can
 * throw.
 */
function platformStorage(): Storage | undefined {
  const fromGlobal = (globalThis as { localStorage?: Storage }).localStorage;
  if (fromGlobal) return fromGlobal;
  return (globalThis as { window?: { localStorage?: Storage } }).window?.localStorage;
}

export const browserStorage: StateStorage = {
  getItem: (name) => {
    try {
      const value = platformStorage()?.getItem(name);
      if (value !== undefined) return value;
    } catch {
      // fall through to the stand-in
    }
    return sharedMemoryStorage.getItem(name);
  },
  setItem: (name, value) => {
    try {
      const storage = platformStorage();
      if (storage) {
        storage.setItem(name, value);
        return;
      }
    } catch {
      // fall through to the stand-in
    }
    sharedMemoryStorage.setItem(name, value);
  },
  removeItem: (name) => {
    try {
      const storage = platformStorage();
      if (storage) {
        storage.removeItem(name);
        return;
      }
    } catch {
      // fall through to the stand-in
    }
    sharedMemoryStorage.removeItem(name);
  },
};

/** Read a key, or `null` when it is unset or storage is unusable. */
export function readStoredValue(name: string): string | null {
  const value = browserStorage.getItem(name);
  return typeof value === "string" ? value : null;
}

/** Write a key, degrading to memory rather than throwing. */
export function writeStoredValue(name: string, value: string): void {
  browserStorage.setItem(name, value);
}

/** Drop a key, degrading to memory rather than throwing. */
export function removeStoredValue(name: string): void {
  browserStorage.removeItem(name);
}

export function createDebouncedStorage(
  baseStorage: Partial<StateStorage> | null | undefined,
  debounceMs: number = 300,
): DebouncedStorage {
  const resolvedStorage = resolveStorage(baseStorage);
  const debouncedSetItem = new Debouncer(
    (name: string, value: string) => {
      resolvedStorage.setItem(name, value);
    },
    { wait: debounceMs },
  );

  return {
    getItem: (name) => resolvedStorage.getItem(name),
    setItem: (name, value) => {
      debouncedSetItem.maybeExecute(name, value);
    },
    removeItem: (name) => {
      debouncedSetItem.cancel();
      resolvedStorage.removeItem(name);
    },
    flush: () => {
      debouncedSetItem.flush();
    },
  };
}
